// packages/core/src/types.ts
var OEM_SCHEMA_VERSION = 1;

// packages/core/src/pointLink.ts
var BASE62_ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
var BASE62_BASE = BigInt(BASE62_ALPHABET.length);
var POINT_ID_PERMUTATION_MOD = 1n << 36n;
var POINT_ID_PERMUTATION_MULTIPLIER = 25214903917n;
var POINT_ID_PERMUTATION_OFFSET = 11n;
var POINT_ID_TOKEN_LENGTH = 7;
function encodeOEMPointToken(pointId) {
  if (!/^\d+$/.test(pointId)) throw new Error(`Invalid OEM point ID: ${pointId}`);
  const id = BigInt(pointId);
  if (id >= POINT_ID_PERMUTATION_MOD)
    throw new Error(`OEM point ID is outside the short-link range: ${pointId}`);
  let value = (id * POINT_ID_PERMUTATION_MULTIPLIER + POINT_ID_PERMUTATION_OFFSET) % POINT_ID_PERMUTATION_MOD;
  let encoded = "";
  do {
    encoded = BASE62_ALPHABET[Number(value % BASE62_BASE)] + encoded;
    value /= BASE62_BASE;
  } while (value > 0n);
  return encoded.padStart(POINT_ID_TOKEN_LENGTH, "0");
}
function createOEMPointUrl(pointId) {
  return `https://oem.re/${encodeOEMPointToken(pointId)}`;
}

// packages/core/src/errors.ts
var OEMError = class extends Error {
  constructor(code, operation, message, path, options) {
    super(path ? `${path}: ${message}` : message, options);
    this.code = code;
    this.operation = operation;
    this.path = path;
  }
  name = "OEMError";
};
function invalid(path, message) {
  throw new OEMError("INVALID_INPUT", "validate", message, path);
}
function resourceError(operation, cause) {
  return cause instanceof OEMError ? cause : new OEMError(
    "RESOURCE_FAILED",
    operation,
    cause instanceof Error ? cause.message : String(cause),
    void 0,
    { cause }
  );
}

// packages/core/src/abort.ts
function withOEMAbort(operation, signal) {
  if (!signal) return operation;
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason ?? new DOMException("The operation was aborted", "AbortError"));
    if (signal.aborted) {
      void operation.catch(() => void 0);
      abort();
      return;
    }
    signal.addEventListener("abort", abort, { once: true });
    operation.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}

// packages/core/src/validation.ts
var isObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);
var isText = (value) => typeof value === "string" && value.length > 0;
var isPair = (value) => Array.isArray(value) && value.length === 2 && value.every(Number.isFinite);
var isTransform = (value) => value === void 0 || isObject(value) && ["scaleX", "scaleZ", "offsetX", "offsetZ"].every((key) => Number.isFinite(value[key])) && value.scaleX !== 0 && value.scaleZ !== 0;
function checkOEMManifestVersion(value) {
  if (!isObject(value) || value.schemaVersion !== OEM_SCHEMA_VERSION)
    invalid("manifest.schemaVersion", "Unsupported or invalid OEM manifest");
  return value;
}
function validateOEMManifest(value) {
  if (!isObject(value) || value.schemaVersion !== OEM_SCHEMA_VERSION)
    invalid("manifest.schemaVersion", "Unsupported or invalid OEM manifest");
  if (!isText(value.releaseId) || !isText(value.gameVersion)) invalid("manifest", "Missing release identity");
  if (value.gameBuild !== void 0 && (typeof value.gameBuild !== "string" || !/^\d+-\d+$/.test(value.gameBuild)))
    invalid("manifest.gameBuild", "Invalid AKEData build");
  if (value.gameVersionLabel !== void 0 && (typeof value.gameVersionLabel !== "string" || value.gameVersionLabel !== `${String(value.gameVersion).replaceAll("_", ".")} (${value.gameBuild})`)) {
    invalid("manifest.gameVersionLabel", "Combined game and data version does not match");
  }
  if (!Array.isArray(value.regions) || !value.regions.length) invalid("manifest.regions", "Expected regions");
  const ids = /* @__PURE__ */ new Set();
  for (const [index, region] of value.regions.entries()) {
    const path = `manifest.regions[${index}]`;
    if (!isObject(region) || !isText(region.id)) invalid(path, "Expected a region with an ID");
    if (ids.has(region.id)) invalid(`${path}.id`, "Duplicate region");
    ids.add(region.id);
    if (!isPair(region.dimensions) || region.dimensions.some((n) => n <= 0))
      invalid(`${path}.dimensions`, "Expected two positive dimensions");
    if (!isObject(region.boundsOffset) || !Number.isFinite(region.boundsOffset.x) || !Number.isFinite(region.boundsOffset.z))
      invalid(`${path}.boundsOffset`, "Expected finite x/z offsets");
    if (!Number.isFinite(region.tileSize) || region.tileSize <= 0)
      invalid(`${path}.tileSize`, "Expected a positive tile size");
    const { minZoom, maxZoom, maxNativeZoom } = region;
    if (![minZoom, maxZoom, maxNativeZoom].every(Number.isFinite) || minZoom > maxZoom || maxNativeZoom > maxZoom || !Number.isFinite(2 ** maxNativeZoom) || 2 ** maxNativeZoom === 0)
      invalid(path, "Invalid zoom range");
    if (!isObject(region.initialView) || region.initialView.regionId !== region.id || !["x", "z", "zoom"].every((key) => Number.isFinite(region.initialView[key])))
      invalid(`${path}.initialView`, "Expected a finite view in this region");
    if (!Array.isArray(region.floors) || !region.floors.some((f) => isObject(f) && f.id === "M"))
      invalid(`${path}.floors`, "Main floor M is required");
    const floors = /* @__PURE__ */ new Set();
    for (const [i, floor] of region.floors.entries()) {
      if (!isObject(floor) || !isText(floor.id) || !isText(floor.tileTemplate))
        invalid(`${path}.floors[${i}]`, "Expected floor ID and tile template");
      if (floors.has(floor.id)) invalid(`${path}.floors[${i}].id`, "Duplicate floor");
      floors.add(floor.id);
    }
    if (!Array.isArray(region.subregions)) invalid(`${path}.subregions`, "Expected subregions");
    const subregions = /* @__PURE__ */ new Set();
    for (const [i, subregion] of region.subregions.entries()) {
      const subpath = `${path}.subregions[${i}]`;
      if (!isObject(subregion) || !isText(subregion.id) || !isText(subregion.key))
        invalid(subpath, "Expected subregion ID and key");
      if (subregions.has(subregion.id)) invalid(`${subpath}.id`, "Duplicate subregion");
      subregions.add(subregion.id);
      if (subregion.bounds !== void 0 && (!Array.isArray(subregion.bounds) || subregion.bounds.length !== 2 || !subregion.bounds.every(isPair)))
        invalid(`${subpath}.bounds`, "Expected two finite corners");
      if (!isTransform(subregion.gameTransform))
        invalid(`${subpath}.gameTransform`, "Invalid coordinate transform");
    }
    if (!isTransform(region.gameTransform)) invalid(`${path}.gameTransform`, "Invalid coordinate transform");
    if (!Array.isArray(region.points)) invalid(`${path}.points`, "Expected point resource references");
    if (!isObject(region.coverage)) invalid(`${path}.coverage`, "Expected coverage lookup");
    if (!isObject(region.locales)) invalid(`${path}.locales`, "Expected region names");
  }
  if (!ids.has(String(value.defaultRegionId))) invalid("manifest.defaultRegionId", "Unknown OEM region");
  if (!isObject(value.types) || !isText(value.types.path))
    invalid("manifest.types", "Missing point type resource");
  if (!isObject(value.locales)) invalid("manifest.locales", "Expected locale resources");
  if (!isText(value.fallbackLocale) || !isObject(value.controls) || !Object.hasOwn(value.controls, value.fallbackLocale))
    invalid("manifest.controls", "Fallback control messages are unavailable");
  const controls = value.controls[value.fallbackLocale];
  if (!isObject(controls) || !["layerSelect", "zoomIn", "zoomOut", "brandName", "termsOfService"].every((key) => isText(controls[key])))
    invalid("manifest.controls", "Fallback control messages are unavailable");
  if (value.fonts !== void 0 && !Array.isArray(value.fonts))
    invalid("manifest.fonts", "Expected font resources");
  return value;
}

// packages/core/src/resources.ts
function resolveOEMAsset(base, assetPath) {
  if (typeof base !== "string" || typeof assetPath !== "string" || !assetPath.trim())
    invalid("resource.path", "Expected a relative resource path");
  if (/^[a-z][a-z\d+.-]*:/i.test(assetPath) || assetPath.startsWith("//") || // eslint-disable-next-line no-control-regex
  /[\\\x00-\x20]/.test(assetPath)) {
    invalid("resource.path", "Resource paths must be relative to their configured origin");
  }
  let decoded;
  try {
    decoded = decodeURIComponent(assetPath);
  } catch {
    return invalid("resource.path", "Invalid URL encoding");
  }
  if (decoded.split(/[/?#]/).includes("..") || decoded.includes("\\"))
    invalid("resource.path", "Resource traversal is not allowed");
  let origin;
  try {
    origin = new URL(base);
  } catch {
    return invalid("resources.baseUrl", "Expected an absolute HTTP(S) resource base");
  }
  if (!["https:", "http:"].includes(origin.protocol) || origin.username || origin.password || origin.search || origin.hash)
    invalid("resources.baseUrl", "Expected an HTTP(S) resource base");
  return `${base.replace(/\/$/, "")}/${assetPath.replace(/^\//, "")}`;
}

// packages/core/src/manifest.ts
var defaultOEMResources = Object.freeze({
  baseUrl: "https://data.opendfieldmap.org",
  manifestPath: "/channels/stable.json"
});
function getOEMRegion(manifest, id) {
  const region = manifest.regions.find((entry) => entry.id === id);
  if (!region) throw new Error(`Unknown OEM region: ${id}`);
  return region;
}
function getOEMDefaultFloor(region) {
  return region.floors.find((floor) => floor.id === "M")?.id ?? region.floors[0]?.id ?? "M";
}
function normalizeOEMLocale(requested, available, fallback) {
  const locale = requested.replace(/_/g, "-").toLowerCase();
  const exact = available.find((entry) => entry.toLowerCase() === locale);
  if (exact) return exact;
  if (locale.startsWith("zh")) {
    const chinese = /hant|tw|hk|mo/.test(locale) ? "zh-HK" : "zh-CN";
    if (available.includes(chinese)) return chinese;
  }
  return available.find((entry) => entry.toLowerCase().split("-")[0] === locale.split("-")[0]) ?? fallback;
}
async function fetchOEMJson(url, signal) {
  signal?.throwIfAborted();
  try {
    const response = await withOEMAbort(
      fetch(url, { signal, credentials: "omit", headers: { Accept: "application/json" } }),
      signal
    );
    if (!response.ok)
      throw resourceError("fetch", new Error(`Static resource failed (${response.status}): ${url}`));
    return await withOEMAbort(response.json(), signal);
  } catch (error) {
    signal?.throwIfAborted();
    throw resourceError("fetch", error);
  }
}
async function loadOEMManifest(resources, signal) {
  const value = await fetchOEMJson(
    resolveOEMAsset(resources?.baseUrl, resources?.manifestPath),
    signal
  );
  return checkOEMManifestVersion(
    value && typeof value === "object" && "manifest" in value ? await fetchOEMJson(resolveOEMAsset(resources.baseUrl, value.manifest?.path), signal) : value
  );
}

// packages/core/src/coordinates.ts
function toOEMLeafletPosition(position, region) {
  if (position.regionId !== region.id || !Number.isFinite(position.x) || !Number.isFinite(position.z))
    throw new Error("Invalid OEM position");
  const scale = 2 ** region.maxNativeZoom;
  return [position.z / scale, position.x / scale];
}
function fromOEMLeafletPosition(lat, lng, region, floorId = "M") {
  const scale = 2 ** region.maxNativeZoom;
  return { regionId: region.id, x: lng * scale, z: lat * scale, floorId };
}
function toOEMMapPosition(position, region) {
  if (position.regionId !== region.id || !Number.isFinite(position.x) || !Number.isFinite(position.z)) {
    throw new Error("Invalid OEM position");
  }
  const scale = 2 ** region.maxNativeZoom;
  return {
    regionId: region.id,
    ...position.subregionId ? { subregionId: position.subregionId } : {},
    x: position.x / scale,
    z: position.z / scale,
    floorId: position.floorId
  };
}
function fromOEMMapPosition(position, region) {
  if (position.regionId !== region.id || !Number.isFinite(position.x) || !Number.isFinite(position.z)) {
    throw new Error("Invalid OEM map position");
  }
  const scale = 2 ** region.maxNativeZoom;
  return {
    regionId: region.id,
    ...position.subregionId ? { subregionId: position.subregionId } : {},
    x: position.x * scale,
    z: position.z * scale,
    floorId: position.floorId
  };
}
function toOEMLeafletMapPosition(position) {
  if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.z))
    throw new Error("Invalid OEM map position");
  return [position.z, position.x];
}
function gameToOEMPosition(raw, region, floorId = "M", subregionId) {
  if (![raw.x, raw.y, raw.z].every(Number.isFinite)) throw new Error("Invalid game position");
  return fromOEMMapPosition(gameXZToOEMPosition(raw, region, floorId, subregionId), region);
}
function gameXZToOEMPosition(raw, region, floorId = "M", subregionId) {
  if (!Number.isFinite(raw.x) || !Number.isFinite(raw.z)) throw new Error("Invalid game position");
  const transform = getOEMGameTransform(region, subregionId);
  return {
    regionId: region.id,
    ...subregionId ? { subregionId } : {},
    x: raw.x * transform.scaleX + transform.offsetX,
    z: raw.z * transform.scaleZ + transform.offsetZ,
    floorId
  };
}
function oemToGamePosition(position, region, subregionId) {
  if (position.regionId !== region.id || !Number.isFinite(position.x) || !Number.isFinite(position.z)) {
    throw new Error("Invalid OEM position");
  }
  return mapToGameXZPosition(toOEMMapPosition(position, region), region, subregionId);
}
function mapToGameXZPosition(position, region, subregionId) {
  if (position.regionId !== region.id || !Number.isFinite(position.x) || !Number.isFinite(position.z)) {
    throw new Error("Invalid OEM map position");
  }
  const transform = getOEMGameTransform(region, position.subregionId ?? subregionId);
  return {
    x: (position.x - transform.offsetX) / transform.scaleX,
    z: (position.z - transform.offsetZ) / transform.scaleZ
  };
}
function getOEMGameTransform(region, subregionId) {
  if (subregionId) {
    const subregion = region.subregions.find((entry) => entry.id === subregionId);
    if (!subregion) throw new Error(`Unknown OEM subregion ${subregionId} in ${region.id}`);
    if (subregion.gameTransform) return subregion.gameTransform;
  }
  return region.gameTransform ?? { scaleX: 1, scaleZ: 1, offsetX: 0, offsetZ: 0 };
}
function getOEMFloorFromTier(tier) {
  return tier === 0 ? "M" : `${tier < 0 ? "B" : "L"}${Math.abs(Math.trunc(tier))}`;
}
function pixelToMapPosition(position, region) {
  return { ...toOEMMapPosition(position, region), space: "map" };
}
function mapToPixelPosition(position, region) {
  return { ...fromOEMMapPosition(position, region), space: "pixel" };
}
function gameXZToMapPosition(position, region, floorId = "M", subregionId) {
  return { ...gameXZToOEMPosition(position, region, floorId, subregionId), space: "map" };
}
function pixelToGameXZPosition(position, region, subregionId) {
  return { ...oemToGamePosition(position, region, subregionId), space: "game" };
}

// packages/core/src/snapshot.ts
function createOEMCoordinateSnapshot(position, manifest, subregionResolution = position.subregionId ? "provided" : "unresolved") {
  const region = getOEMRegion(manifest, position.regionId);
  const subregion = position.subregionId ? region.subregions.find((item) => item.id === position.subregionId) : void 0;
  const needsSubregion = region.subregions.some((item) => item.gameTransform);
  const reliable = subregionResolution === "provided" || subregionResolution === "geometry";
  const transform = subregion?.gameTransform ?? region.gameTransform;
  const gameResolution = needsSubregion && (!subregion || !reliable) ? "subregion-unresolved" : transform ? "resolved" : "transform-unavailable";
  return Object.freeze({
    mapPosition: Object.freeze({ ...position, space: "map" }),
    pixelPosition: Object.freeze({ ...fromOEMMapPosition(position, region), space: "pixel" }),
    gamePosition: gameResolution === "resolved" ? Object.freeze({ ...mapToGameXZPosition(position, region), space: "game" }) : null,
    context: Object.freeze({
      schemaVersion: 1,
      releaseId: manifest.releaseId,
      gameVersion: manifest.gameVersion,
      ...manifest.gameBuild ? { gameBuild: manifest.gameBuild } : {},
      ...manifest.gameVersionLabel ? { gameVersionLabel: manifest.gameVersionLabel } : {}
    }),
    subregionResolution,
    gameResolution
  });
}
export {
  OEMError,
  OEM_SCHEMA_VERSION,
  checkOEMManifestVersion,
  createOEMCoordinateSnapshot,
  createOEMPointUrl,
  defaultOEMResources,
  encodeOEMPointToken,
  fetchOEMJson,
  fromOEMLeafletPosition,
  fromOEMMapPosition,
  gameToOEMPosition,
  gameXZToMapPosition,
  gameXZToOEMPosition,
  getOEMDefaultFloor,
  getOEMFloorFromTier,
  getOEMGameTransform,
  getOEMRegion,
  invalid,
  loadOEMManifest,
  mapToGameXZPosition,
  mapToPixelPosition,
  normalizeOEMLocale,
  oemToGamePosition,
  pixelToGameXZPosition,
  pixelToMapPosition,
  resolveOEMAsset,
  resourceError,
  toOEMLeafletMapPosition,
  toOEMLeafletPosition,
  toOEMMapPosition,
  validateOEMManifest,
  withOEMAbort
};
//# sourceMappingURL=index.js.map
